import { useCuentaBancariaTesoreriaById } from "api/CuentaBancaria.hook"
import { CampoPersonalizadoDtoProps } from "api/DTOs/CampoPersonalizadoDto"
import { ComisionReadingDtoProps } from "api/DTOs/ComisionReadingDto"
import { PrestamoReadingDtoProps } from "api/DTOs/PrestamoReadingDto"
import { PERIODO_AMORTIZACION } from "api/Types/PeriodoAmortizacion"
import { DATE_DD_MM_YYYY } from "common/components/FilterMenu/constants"
import { ESTADO_TIPO_COMISION } from "common/utils/constants"
import { formatCurrency } from "common/utils/currency"
import { formatDate } from "common/utils/date"
import { nanoid } from "nanoid"
import { Skeleton } from "primereact/skeleton"
import { useTranslation } from "react-i18next"
import { TIPO_IMPORTE } from "../../pages/PrestamoDetail/constants"

const DetailSideInfo = ({
  prestamo,
}: {
  prestamo: PrestamoReadingDtoProps | null | undefined
}) => {
  const { cuentaBancariaById } = useCuentaBancariaTesoreriaById(
    prestamo?.cuentaBancariaId
  )

  const { t } = useTranslation()
  return (
    <section className='tw-flex tw-flex-col tw-w-full tw-h-full'>
      <div className='tw-flex tw-flex-col tw-bg-white tw-shadow tw-rounded-lg tw-p-6'>
        <p className=' tw-m-0 tw-font-medium tw-text-lg tw-text-start'>
          {t("prestamos.idView.details.titleSummary")}
        </p>
        <div className='tw-flex tw-flex-col tw-mt-3'>
          <div className='tw-flex tw-flex-row tw-justify-between tw-items-end tw-my-2 tw-flex-wrap tw-h-fit'>
            <span className='tw-text-base'>{`${t(
              "prestamos.idView.details.importLabel"
            )}`}</span>
            {prestamo?.importe ? (
              <span className='tw-text-2xl tw-font-bold'>
                {formatCurrency(prestamo?.importe)}
              </span>
            ) : (
              <Skeleton className='!tw-h-8 !tw-w-1/4' />
            )}
          </div>
          <div className='tw-flex tw-flex-row tw-justify-between tw-items-end tw-my-2 tw-flex-wrap'>
            <span className='tw-text-base'>{`${t(
              "prestamos.idView.details.whiteOffLabel"
            )}`}</span>
            {prestamo?.totalAmortizado !== undefined ? (
              <span className='tw-text-2xl tw-font-bold'>
                {formatCurrency(prestamo?.totalAmortizado)}
              </span>
            ) : (
              <Skeleton className='!tw-h-8 !tw-w-1/4' />
            )}
          </div>

          <div className='tw-flex tw-flex-row tw-justify-between tw-items-end tw-mt-2 tw-flex-wrap'>
            <span className='tw-text-base'>{`${t(
              "prestamos.idView.details.OutstandingPrincipalLabel"
            )}`}</span>
            {prestamo?.importePendiente !== undefined ? (
              <span className='tw-text-2xl tw-font-bold'>
                {formatCurrency(prestamo?.importePendiente)}
              </span>
            ) : (
              <Skeleton className='!tw-h-8 !tw-w-1/4' />
            )}
          </div>
        </div>
      </div>

      <div className='tw-flex tw-flex-col tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mt-4 tw-h-[calc(100%-224px)] tw-overflow-y-auto'>
        <p className=' tw-m-0 tw-font-medium tw-text-lg tw-text-start tw-mb-4'>
          {t("prestamos.idView.details.titleDetail")}
        </p>
        <div className='tw-grid tw-grid-cols-3 tw-gap-6 lg:tw-gap-2 2xl:tw-gap-6 tw-mb-6'>
          <div className='tw-flex tw-flex-col tw-items-start tw-col-span-2 lg:tw-col-span-3 3xl:tw-col-span-2'>
            <span className='tw-text-xs tw-uppercase'>
              {t("prestamos.idView.details.contactLabel")}
            </span>
            {prestamo?.razonSocial ? (
              <span className='tw-base tw-truncate tw-w-full tw-text-start'>
                {prestamo?.razonSocial}
              </span>
            ) : (
              <Skeleton className='!tw-h-6 !tw-w-3/4' />
            )}
          </div>
          <div className='tw-flex tw-flex-col tw-items-start tw-col-span-1 lg:tw-col-span-3 3xl:tw-col-span-1'>
            <span className='tw-text-xs tw-uppercase'>
              {t("prestamos.idView.details.dateLabel")}
            </span>
            {prestamo?.fecha ? (
              <span className='tw-text-base tw-text-start tw-text-nowrap'>
                {formatDate(prestamo?.fecha, DATE_DD_MM_YYYY)}
              </span>
            ) : (
              <Skeleton className='!tw-h-6 !tw-w-2/3' />
            )}
          </div>
        </div>
        <div className='tw-grid tw-grid-cols-4 tw-gap-6 lg:tw-gap-2 2xl:tw-gap-6'>
          <div className='tw-flex tw-flex-col tw-items-start :tw-col-span-1 lg:tw-col-span-4 2xl:tw-col-span-1'>
            <span className='tw-text-xs tw-uppercase'>
              {t("prestamos.idView.details.periodLabel")}
            </span>
            {prestamo?.periodoAmortizacion ? (
              <span className='tw-base tw-w-full tw-text-start'>
                {prestamo?.periodoAmortizacion === PERIODO_AMORTIZACION.MENSUAL
                  ? t("others.monthlyLabel")
                  : t("others.annualLabel")}
              </span>
            ) : (
              <Skeleton className='!tw-h-6 !tw-w-2/3' />
            )}
          </div>
          <div className='tw-flex tw-flex-col tw-items-start tw-col-span-2 lg:tw-col-span-4 2xl:tw-col-span-2'>
            <span className='tw-text-xs tw-uppercase'>
              {`${t("prestamos.idView.details.durationLabel")} (${t(
                "prestamos.idView.details.monthsLabel"
              )})`}
            </span>
            {prestamo?.duracion !== undefined ? (
              <span className='tw-base tw-w-full tw-text-start'>
                {prestamo?.duracion ?? "Indefinida"}
              </span>
            ) : (
              <Skeleton className='!tw-h-6 !tw-w-2/3' />
            )}
          </div>
          <div className='tw-flex tw-flex-col tw-items-start tw-col-span-1 lg:tw-col-span-4 2xl:tw-col-span-1'>
            <span className='tw-text-xs tw-uppercase'>
              {t("prestamos.idView.details.taxLabel")}
            </span>
            {prestamo?.interes !== undefined ? (
              <span className='tw-base tw-w-full tw-text-start'>
                {prestamo?.interes?.importe ?? 0} %
              </span>
            ) : (
              <Skeleton className='!tw-h-6 !tw-w-full' />
            )}
          </div>
        </div>
        <div className='tw-flex tw-flex-col tw-mb-6'>
          {cuentaBancariaById?.iban && (
            <div className='tw-flex tw-flex-col tw-items-start tw-w-full tw-mt-2.5'>
              <span className='tw-text-xs tw-uppercase'>
                {t("prestamos.idView.details.banckAccountLabel")}
              </span>
              <span className='tw-base tw-w-full tw-truncate tw-text-start'>
                {cuentaBancariaById?.iban}
              </span>
            </div>
          )}
          {prestamo?.descripcion && (
            <div className='tw-flex tw-flex-col tw-items-start tw-w-full tw-h-auto tw-mt-2.5'>
              <span className='tw-text-xs tw-uppercase'>
                {t("prestamos.idView.details.descriptionLabel")}
              </span>
              <span className='tw-base tw-w-full tw-text-start tw-h-auto'>
                {prestamo?.descripcion}
              </span>
            </div>
          )}
        </div>
        {prestamo?.interes?.cuentaContable && (
          <div className='tw-flex tw-flex-col tw-items-start tw-w-full tw-h-auto tw-mb-4'>
            <span className='tw-text-xs tw-uppercase'>
              {t("prestamos.idView.details.accountInteresLabel")}
            </span>
            <span className='tw-text-info-100 tw-base tw-w-full tw-text-start tw-h-auto'>
              {prestamo?.interes?.cuentaContable}
            </span>
          </div>
        )}
        {prestamo?.cuentaContable && (
          <div className='tw-flex tw-flex-col tw-items-start tw-w-full tw-h-auto'>
            <span className='tw-text-xs tw-uppercase'>
              {t("prestamos.idView.details.quoteAccountFinLabel")}
            </span>
            <span className='tw-text-info-100 tw-base tw-w-full tw-text-start tw-h-auto'>
              {prestamo?.cuentaContable}
            </span>
          </div>
        )}
        {!!prestamo?.comisiones?.length && (
          <div className='tw-flex tw-flex-col tw-mt-6'>
            <p className=' tw-m-0 tw-font-medium tw-text-base tw-text-start tw-mb-3'>
              {t("prestamos.idView.details.titleComision")}
            </p>
            <div className='tw-flex tw-flex-col tw-justify-start'>
              {prestamo?.comisiones?.map(
                (
                  {
                    cuentaContable,
                    importe,
                    tipoComision,
                  }: ComisionReadingDtoProps,
                  index: number
                ) => {
                  return (
                    <span
                      key={nanoid()}
                      className={`${
                        prestamo?.comisiones?.length === index - 1
                          ? ""
                          : "tw-mb-2"
                      }`}
                    >
                      <p className='tw-text-start tw-h-auto xl:tw-text-ellipsis tw-flex tw-flex-col tw-items-start'>
                        <span className='tw-flex tw-items-center'>
                          <span className='tw-text-base'>
                            {tipoComision
                              ? ESTADO_TIPO_COMISION(t)?.[tipoComision].fullText
                              : t("prestamos.form.commissionLabel")}
                          </span>
                          <span className='tw-ml-1 tw-text-alternative2'>
                            {importe?.valor}
                            {TIPO_IMPORTE?.[importe?.tipoImporte ?? 1]}
                          </span>
                        </span>
                        {cuentaContable && (
                          <span className='tw-text-info-100 tw-max-h-12 tw-inline tw-flex-1 tw-w-full tw-truncate'>
                            {cuentaContable}
                          </span>
                        )}
                      </p>
                    </span>
                  )
                }
              )}
            </div>
          </div>
        )}
        {!!prestamo?.camposPersonalizados?.length && (
          <div className='tw-flex tw-flex-col tw-mt-6'>
            <p className=' tw-m-0 tw-font-medium tw-text-base tw-text-start tw-mb-3'>
              {t("prestamos.idView.details.titleCamPersonal")}
            </p>
            <div className='tw-flex tw-flex-col tw-justify-start'>
              {prestamo?.camposPersonalizados?.map(
                (
                  { clave, valor }: CampoPersonalizadoDtoProps,
                  index: number
                ) => (
                  <div
                    key={nanoid()}
                    className={`${
                      prestamo?.camposPersonalizados?.length === index - 1
                        ? ""
                        : "tw-mb-2"
                    } tw-flex tw-flex-row`}
                  >
                    <span className='tw-font-normal tw-mr-1'>{clave}</span>-
                    <span className='tw-font-normal tw-text-alternative2 tw-ml-1 tw-truncate'>
                      {valor}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default DetailSideInfo
